import { ErrorMessage, Field, Label } from '@/components/base/fieldset'
import { Input } from '@/components/base/input'
import { Button } from '@/components/base/button'
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/solid'
import { Text } from '@/components/base/text'
import clsx from 'clsx'
import type { ReactNode } from 'react'

interface Props<T> {
  attributes: T
  setAttribute: <K extends keyof T>(key: K, value: T[K]) => void
  mutationError?: string
  fieldErrors?: { [field: string]: string }
}

type FieldTypeMap = {
  text: string
  number: number
}
type FieldType = keyof FieldTypeMap
type KeysOfType<T, V> = {
  [K in keyof T]-?: NonNullable<T[K]> extends V ? K : never
}[keyof T]

export const builder = <T,>({ attributes, setAttribute, mutationError, fieldErrors }: Props<T>) => {
  const set = (field: keyof T, value: unknown) => setAttribute(field, value as T[typeof field])

  const fieldFor = <F extends FieldType>(
    type: F,
    field: KeysOfType<T, FieldTypeMap[F]>,
    disabled: boolean,
  ) => {
    const fieldError = fieldErrors?.[String(field)]

    // this keeps the component controlled, even if we haven't finished init yet
    const value = attributes[field] as string ?? '' as string | number

    const common = { invalid: !!fieldError, value, disabled }

    const onChangeText = (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => set(field, e.target.value)
    const onChangeNumber = (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => set(field, parseFloat(e.target.value))

    switch (type) {
      case 'text': return <Input { ...common } type="text" onChange={ onChangeText } />
      case 'number': return <Input { ...common } step={ 0.01 } type="number" onChange={ onChangeNumber } />
    }
  }

  const buildField = <F extends FieldType>({
    field, type, disabled = false, ...props
  }: {
    name: string
    type: F
    field: KeysOfType<T, FieldTypeMap[F]>
    disabled?: boolean
    hideLabel?: boolean
  }) => buildCustomField({ ...props, field, input: fieldFor(type, field, disabled) })

  const buildCustomField = <F extends FieldType>({
    name, field, input, disabled = false, hideLabel = false
  }: {
    name: string
    field: KeysOfType<T, FieldTypeMap[F]>
    input: ReactNode
    disabled?: boolean
    hideLabel?: boolean
  }) => {
    const fieldError = fieldErrors?.[field as string]

    return (
      <Field disabled={ disabled }>
        { !hideLabel && <Label>{ name }</Label> }
        { input }
        { fieldError && <ErrorMessage>{ fieldError }</ErrorMessage> }
      </Field>
    )
  }

  const buildQueryError = () => {
    if (!mutationError) return <></>

    return (
      <Field className="flex flex-row gap-2 items-center">
        <Text>An error prevented saving:</Text> <ErrorMessage>{ mutationError }</ErrorMessage>
      </Field>
    )
  }

  const buildBaseError = () => {
    const fieldError = fieldErrors?.base
    if (!fieldError) return <></>

    return (
      <Field className="flex flex-row gap-2 items-center">
        <Text>An error prevented saving:</Text> <ErrorMessage>{ fieldError }</ErrorMessage>
      </Field>
    )
  }

  const saveButton = (
    save: () => void,
    loading: boolean,
    saveSuccess?: boolean,
    prompt?: string,
    color?: 'red' | 'green'
  ) => (
    <div className="flex flex-row items-center justify-end gap-3">
      <div className="relative w-8 h-8">
        <CheckIcon
          className={ clsx(
            'absolute inset-0 transition duration-150 text-green-600 h-8 w-8',
            saveSuccess == true ? 'opacity-100' : 'opacity-0'
          ) }
        />
        <XMarkIcon
          className={ clsx(
            'absolute inset-0 transition duration-150 text-red-600 h-8 w-8',
            saveSuccess == false ? 'opacity-100' : 'opacity-0'
          ) }
        />
      </div>
      <Button color={ color || 'green' } disabled={ loading } onClick={ save }>
        { loading ? 'Saving...' : prompt || 'Save' }
      </Button>
    </div>
  )

  return { buildField, buildCustomField, buildQueryError, buildBaseError, saveButton, set }
}
